import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { DollarSign, Zap, Info, CreditCard, Shield, CheckCircle, XCircle, Calendar, Lock, Unlock, RefreshCw, Clock, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = window.__BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to format ISO date to datetime-local value (preserving exact time)
function isoToDatetimeLocal(isoString) {
  if (!isoString) return '';
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return '';
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  } catch {
    return '';
  }
}

// Helper to convert datetime-local value to ISO UTC string
function datetimeLocalToIso(datetimeLocal) {
  if (!datetimeLocal) return '';
  try {
    const [datePart, timePart] = datetimeLocal.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hours, minutes] = timePart.split(':').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day, hours, minutes, 0, 0));
    return date.toISOString();
  } catch {
    return '';
  }
}

export default function AdminPricingSettings() {
  const [loading, setLoading] = useState(true);
  
  // Launch date settings
  const [launchSettings, setLaunchSettings] = useState(null);
  const [launchDateInput, setLaunchDateInput] = useState('');
  const [savingLaunchDate, setSavingLaunchDate] = useState(false);
  
  // Launch pricing mode
  const [launchPricingMode, setLaunchPricingMode] = useState('AUTO');
  const [savingMode, setSavingMode] = useState(false);
  
  // Stripe environment settings
  const [stripeStatus, setStripeStatus] = useState(null);
  const [selectedStripeEnv, setSelectedStripeEnv] = useState('test');
  const [liveConfirmed, setLiveConfirmed] = useState(false);
  const [savingStripeEnv, setSavingStripeEnv] = useState(false);

  useEffect(() => {
    loadLaunchSettings();
    loadStripeStatus();
  }, []);

  const loadStripeStatus = async () => {
    try {
      const res = await fetch(`${API}/stripe/admin/stripe-status`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setStripeStatus(data);
        setSelectedStripeEnv(data.active_env || 'test');
      }
    } catch (error) {
      console.error('Error loading Stripe status:', error);
    }
  };

  const loadLaunchSettings = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/stripe/admin/launch-settings`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setLaunchSettings(data);
        setLaunchPricingMode(data.launch_pricing_mode || 'AUTO');
        if (data.launch_start_date) {
          const localValue = isoToDatetimeLocal(data.launch_start_date);
          setLaunchDateInput(localValue);
        }
      }
    } catch (error) {
      console.error('Error loading launch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveLaunchDate = async () => {
    if (!launchDateInput) {
      toast.error('Please select a date and time');
      return;
    }
    setSavingLaunchDate(true);
    try {
      const isoDate = datetimeLocalToIso(launchDateInput);
      if (!isoDate) {
        toast.error('Invalid date format');
        return;
      }
      const res = await fetch(`${API}/stripe/admin/launch-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ launch_start_date: isoDate })
      });
      if (res.status === 403) {
        toast.error('Launch date is locked and cannot be changed');
        return;
      }
      if (res.ok) {
        toast.success('Launch date updated successfully');
        await loadLaunchSettings();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to update launch date');
      }
    } catch (error) {
      console.error('Error saving launch date:', error);
      toast.error('Failed to save launch date');
    } finally {
      setSavingLaunchDate(false);
    }
  };

  const handleToggleLock = async () => {
    const action = launchSettings?.launch_date_locked ? 'unlock' : 'lock';
    const confirmMsg = launchSettings?.launch_date_locked 
      ? 'Are you sure you want to unlock the launch date? This will allow it to be changed.'
      : 'Are you sure you want to lock the launch date? This makes the launch date permanent for pricing, coupons, trials, and EFPM.';
    if (!window.confirm(confirmMsg)) return;
    try {
      const res = await fetch(`${API}/stripe/admin/launch-settings/toggle-lock`, {
        method: 'POST',
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        await loadLaunchSettings();
        await loadStripeStatus(); // Refresh stripe status as lock affects LIVE eligibility
      } else {
        toast.error('Failed to toggle lock');
      }
    } catch (error) {
      console.error('Error toggling lock:', error);
      toast.error('Failed to toggle lock');
    }
  };

  const handleLaunchPricingModeChange = async (newMode) => {
    setSavingMode(true);
    try {
      const res = await fetch(`${API}/stripe/admin/launch-pricing-mode`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ mode: newMode })
      });
      if (res.ok) {
        const data = await res.json();
        setLaunchPricingMode(newMode);
        toast.success(data.message || `Launch pricing mode set to ${newMode}`);
        await loadLaunchSettings();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to update launch pricing mode');
      }
    } catch (error) {
      console.error('Error updating launch pricing mode:', error);
      toast.error('Failed to update launch pricing mode');
    } finally {
      setSavingMode(false);
    }
  };

  const handleStripeEnvChange = async () => {
    if (selectedStripeEnv === 'live' && !liveConfirmed) {
      toast.error('Please confirm you understand LIVE mode will charge real cards');
      return;
    }
    setSavingStripeEnv(true);
    try {
      const res = await fetch(`${API}/stripe/admin/stripe-env`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          env: selectedStripeEnv,
          confirmed: selectedStripeEnv === 'live' ? liveConfirmed : true
        })
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setLiveConfirmed(false);
        await loadStripeStatus();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to change Stripe environment');
      }
    } catch (error) {
      console.error('Error changing Stripe env:', error);
      toast.error('Failed to change Stripe environment');
    } finally {
      setSavingStripeEnv(false);
    }
  };

  const getPhaseBadgeClass = (phase) => {
    switch (phase) {
      case 'PRELAUNCH': return 'bg-purple-500';
      case 'LAUNCH': return 'bg-pink-500';
      case 'REGULAR': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  const canSelectLive = stripeStatus?.can_switch_to_live && stripeStatus?.live?.ready;

  if (loading) {
    return (
      <div className="container mx-auto p-6 max-w-4xl">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-2">
          <DollarSign className="h-8 w-8 text-green-500" />
          Pricing Settings
        </h1>
        <p className="text-[#8A93A6]">
          Manage launch timing, pricing, and Stripe configuration
        </p>
      </div>

      {/* Stripe Environment Control - NEW SECTION */}
      <Card className="glass mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-blue-500" />
            Stripe Environment
            <Badge 
              variant="outline" 
              className={stripeStatus?.active_env === 'live' 
                ? 'ml-2 text-red-500 border-red-500/30' 
                : 'ml-2 text-blue-500 border-blue-500/30'
              }
            >
              {stripeStatus?.active_env?.toUpperCase() || 'TEST'}
            </Badge>
          </CardTitle>
          <CardDescription>
            Control which Stripe environment is active for payments
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Environment Selector */}
          <div className="flex items-center justify-between p-4 bg-[#1A1D25] rounded-lg">
            <div className="flex-1">
              <Label className="text-base font-medium text-white">Active Environment</Label>
              <p className="text-sm text-[#8A93A6] mt-1">
                {stripeStatus?.active_env === 'live' 
                  ? '🔴 LIVE - Real charges will occur!' 
                  : '🧪 TEST - No real charges'}
              </p>
            </div>
            <Select 
              value={selectedStripeEnv} 
              onValueChange={setSelectedStripeEnv}
              disabled={savingStripeEnv}
            >
              <SelectTrigger className="w-[140px] bg-[#1A1D25] border-white/10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="test">
                  <span className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-blue-500"></span>
                    TEST
                  </span>
                </SelectItem>
                <SelectItem value="live" disabled={!canSelectLive}>
                  <span className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-red-500"></span>
                    LIVE {!canSelectLive && '(Locked)'}
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* LIVE Mode Safety Gate */}
          {selectedStripeEnv === 'live' && (
            <Alert className="border-red-500/20 bg-red-500/10">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              <AlertDescription className="text-sm text-red-300">
                <strong>⚠️ WARNING:</strong> LIVE mode will charge real credit cards!
                <div className="mt-3 flex items-center gap-2">
                  <Checkbox 
                    id="live-confirm" 
                    checked={liveConfirmed}
                    onCheckedChange={setLiveConfirmed}
                    className="border-red-500"
                  />
                  <label htmlFor="live-confirm" className="text-sm cursor-pointer">
                    I understand LIVE will charge real cards
                  </label>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* LIVE Blockers */}
          {!canSelectLive && stripeStatus?.live_blockers?.length > 0 && (
            <Alert className="border-yellow-500/20 bg-yellow-500/10">
              <Info className="h-4 w-4 text-yellow-500" />
              <AlertDescription className="text-sm text-yellow-300">
                <strong>Cannot switch to LIVE:</strong>
                <ul className="mt-2 list-disc list-inside">
                  {stripeStatus.live_blockers.map((blocker, idx) => (
                    <li key={idx}>{blocker}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {/* Save Button */}
          {selectedStripeEnv !== stripeStatus?.active_env && (
            <Button
              onClick={handleStripeEnvChange}
              disabled={savingStripeEnv || (selectedStripeEnv === 'live' && !liveConfirmed)}
              className={selectedStripeEnv === 'live' ? 'bg-red-600 hover:bg-red-700' : ''}
            >
              {savingStripeEnv ? 'Switching...' : `Switch to ${selectedStripeEnv.toUpperCase()}`}
            </Button>
          )}

          {/* Key Status Panel */}
          <div className="mt-4 p-4 bg-[#1A1D25] rounded-lg">
            <p className="text-sm font-medium text-white mb-3">Key Status</p>
            <div className="grid grid-cols-2 gap-4">
              {/* TEST Keys */}
              <div className="space-y-2">
                <p className="text-xs font-medium text-blue-400">TEST Environment</p>
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-xs">
                    {stripeStatus?.test?.secret_key_present ? (
                      <CheckCircle className="h-3 w-3 text-green-500" />
                    ) : (
                      <XCircle className="h-3 w-3 text-red-500" />
                    )}
                    <span className="text-[#8A93A6]">Secret Key</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    {stripeStatus?.test?.publishable_key_present ? (
                      <CheckCircle className="h-3 w-3 text-green-500" />
                    ) : (
                      <XCircle className="h-3 w-3 text-red-500" />
                    )}
                    <span className="text-[#8A93A6]">Publishable Key</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    {stripeStatus?.test?.webhook_secret_present ? (
                      <CheckCircle className="h-3 w-3 text-green-500" />
                    ) : (
                      <XCircle className="h-3 w-3 text-gray-500" />
                    )}
                    <span className="text-[#8A93A6]">Webhook Secret</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    {stripeStatus?.test?.connect_client_id_present ? (
                      <CheckCircle className="h-3 w-3 text-green-500" />
                    ) : (
                      <XCircle className="h-3 w-3 text-gray-500" />
                    )}
                    <span className="text-[#8A93A6]">Connect Client ID</span>
                  </div>
                </div>
              </div>
              
              {/* LIVE Keys */}
              <div className="space-y-2">
                <p className="text-xs font-medium text-red-400">LIVE Environment</p>
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-xs">
                    {stripeStatus?.live?.secret_key_present ? (
                      <CheckCircle className="h-3 w-3 text-green-500" />
                    ) : (
                      <XCircle className="h-3 w-3 text-red-500" />
                    )}
                    <span className="text-[#8A93A6]">Secret Key</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    {stripeStatus?.live?.publishable_key_present ? (
                      <CheckCircle className="h-3 w-3 text-green-500" />
                    ) : (
                      <XCircle className="h-3 w-3 text-red-500" />
                    )}
                    <span className="text-[#8A93A6]">Publishable Key</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    {stripeStatus?.live?.webhook_secret_present ? (
                      <CheckCircle className="h-3 w-3 text-green-500" />
                    ) : (
                      <XCircle className="h-3 w-3 text-gray-500" />
                    )}
                    <span className="text-[#8A93A6]">Webhook Secret</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    {stripeStatus?.live?.connect_client_id_present ? (
                      <CheckCircle className="h-3 w-3 text-green-500" />
                    ) : (
                      <XCircle className="h-3 w-3 text-gray-500" />
                    )}
                    <span className="text-[#8A93A6]">Connect Client ID</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Mock Mode Warning */}
            {stripeStatus?.mock_mode && (
              <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded">
                <div className="flex items-center gap-2 text-yellow-400 text-xs">
                  <Shield className="h-4 w-4" />
                  <span><strong>Safe Mode:</strong> Running in MOCK - no real Stripe calls</span>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Launch Date Settings */}
      <Card className="glass mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-blue-500" />
            Launch Date Settings
            {launchSettings?.launch_date_locked && (
              <Badge variant="outline" className="ml-2 text-orange-500 border-orange-500/30">
                <Lock className="h-3 w-3 mr-1" /> Locked
              </Badge>
            )}
          </CardTitle>
          <CardDescription>
            Set when your launch window begins. This determines Day 1 for pricing, coupons, trials, and EFPM.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Date + Time Input */}
          <div className="space-y-4">
            <div className="flex items-end gap-4">
              <div className="flex-1">
                <Label htmlFor="launch-datetime" className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Launch Start Date & Time (UTC)
                </Label>
                <Input
                  id="launch-datetime"
                  type="datetime-local"
                  value={launchDateInput}
                  onChange={(e) => setLaunchDateInput(e.target.value)}
                  disabled={launchSettings?.launch_date_locked}
                  className="mt-2 bg-[#1A1D25] border-white/10 text-white"
                />
                <p className="text-xs text-[#8A93A6] mt-1">
                  All times are in UTC. Current input: {launchDateInput ? datetimeLocalToIso(launchDateInput) : 'None'}
                </p>
              </div>
              <Button
                onClick={handleSaveLaunchDate}
                disabled={launchSettings?.launch_date_locked || savingLaunchDate}
                className="bg-blue-500 hover:bg-blue-600"
              >
                {savingLaunchDate ? 'Saving...' : 'Save Date'}
              </Button>
            </div>
            
            {launchSettings?.launch_start_date && (
              <div className="p-3 bg-[#1A1D25]/50 rounded-lg border border-white/5">
                <p className="text-xs text-[#8A93A6]">
                  Stored value: <span className="text-white font-mono">{launchSettings.launch_start_date}</span>
                </p>
              </div>
            )}
          </div>

          {/* Lock Toggle */}
          <div className="flex items-center justify-between p-4 bg-[#1A1D25] rounded-lg">
            <div className="flex-1">
              <Label className="text-base font-medium text-white flex items-center gap-2">
                {launchSettings?.launch_date_locked ? (
                  <><Lock className="h-4 w-4 text-orange-500" /> Launch Date Locked</>
                ) : (
                  <><Unlock className="h-4 w-4 text-green-500" /> Launch Date Unlocked</>
                )}
              </Label>
              <p className="text-sm text-[#8A93A6] mt-1">
                {launchSettings?.launch_date_locked 
                  ? 'Launch date is locked. Required for LIVE Stripe mode.' 
                  : 'Lock the date when ready. Required before switching to LIVE.'}
              </p>
            </div>
            <Button
              onClick={handleToggleLock}
              variant={launchSettings?.launch_date_locked ? "destructive" : "outline"}
              className={launchSettings?.launch_date_locked ? "" : "border-orange-500 text-orange-500 hover:bg-orange-500/10"}
            >
              {launchSettings?.launch_date_locked ? (
                <><Unlock className="h-4 w-4 mr-2" /> Unlock</>
              ) : (
                <><Lock className="h-4 w-4 mr-2" /> Lock Date</>
              )}
            </Button>
          </div>

          {!launchSettings?.launch_date_locked && (
            <Alert className="border-orange-500/20 bg-orange-500/10">
              <Info className="h-4 w-4 text-orange-500" />
              <AlertDescription className="text-sm text-orange-300">
                <strong>Important:</strong> Locking the launch date is required before switching to LIVE Stripe mode.
              </AlertDescription>
            </Alert>
          )}

          {/* Computed State Summary */}
          {launchSettings?.computed_state && (
            <div className="mt-4 p-4 bg-[#1A1D25] rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium text-white">Computed Launch State</p>
                <Button variant="ghost" size="sm" onClick={loadLaunchSettings}>
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-[#8A93A6]">Day Number</p>
                  <p className="text-lg font-bold text-white">{launchSettings.computed_state.day_number}</p>
                </div>
                <div>
                  <p className="text-xs text-[#8A93A6]">Phase</p>
                  <Badge className={getPhaseBadgeClass(launchSettings.computed_state.phase)}>
                    {launchSettings.computed_state.phase}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-[#8A93A6]">Week Index</p>
                  <p className="text-lg font-bold text-white">{launchSettings.computed_state.week_index}</p>
                </div>
                <div>
                  <p className="text-xs text-[#8A93A6]">Founding Seats</p>
                  <p className="text-lg font-bold text-white">{launchSettings.computed_state.founding_seats_remaining}</p>
                </div>
                <div>
                  <p className="text-xs text-[#8A93A6]">Launch Pricing</p>
                  <Badge variant="outline" className={launchSettings.computed_state.launch_pricing_active ? 'text-green-500 border-green-500/30' : 'text-gray-500 border-gray-500/30'}>
                    {launchSettings.computed_state.launch_pricing_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-[#8A93A6]">Pro Trial</p>
                  <Badge variant="outline" className={launchSettings.computed_state.trial_enabled ? 'text-green-500 border-green-500/30' : 'text-gray-500 border-gray-500/30'}>
                    {launchSettings.computed_state.trial_enabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-[#8A93A6]">EFPM</p>
                  <Badge variant="outline" className={launchSettings.computed_state.earn_free_month_enabled ? 'text-green-500 border-green-500/30' : 'text-gray-500 border-gray-500/30'}>
                    {launchSettings.computed_state.earn_free_month_enabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-[#8A93A6]">Active Coupon</p>
                  <p className="text-sm font-medium text-white">{launchSettings.computed_state.active_coupon || 'None'}</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Launch Pricing Mode Control */}
      <Card className="glass mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-pink-500" />
            Launch Pricing Mode
          </CardTitle>
          <CardDescription>
            Control how launch pricing is determined site-wide
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between p-4 bg-[#1A1D25] rounded-lg">
            <div className="flex-1">
              <Label className="text-base font-medium text-white">Pricing Mode</Label>
              <p className="text-sm text-[#8A93A6] mt-1">Choose how launch pricing is calculated</p>
            </div>
            <Select 
              value={launchPricingMode} 
              onValueChange={handleLaunchPricingModeChange}
              disabled={savingMode}
            >
              <SelectTrigger className="w-[180px] bg-[#1A1D25] border-white/10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="AUTO">
                  <span className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-blue-500"></span>
                    AUTO (by date)
                  </span>
                </SelectItem>
                <SelectItem value="FORCE_ON">
                  <span className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-green-500"></span>
                    FORCE ON
                  </span>
                </SelectItem>
                <SelectItem value="FORCE_OFF">
                  <span className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-red-500"></span>
                    FORCE OFF
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className={`p-4 rounded-lg border-2 transition-all ${launchPricingMode === 'AUTO' ? 'border-blue-500/50 bg-blue-500/10' : 'border-white/5 bg-[#1A1D25]'}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="h-3 w-3 rounded-full bg-blue-500"></span>
                <p className="text-sm font-medium text-white">AUTO</p>
              </div>
              <p className="text-xs text-[#8A93A6]">
                Launch pricing follows the launch date automatically. Active during Days 1-30, inactive otherwise.
              </p>
            </div>
            <div className={`p-4 rounded-lg border-2 transition-all ${launchPricingMode === 'FORCE_ON' ? 'border-green-500/50 bg-green-500/10' : 'border-white/5 bg-[#1A1D25]'}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="h-3 w-3 rounded-full bg-green-500"></span>
                <p className="text-sm font-medium text-white">FORCE ON</p>
              </div>
              <p className="text-xs text-[#8A93A6]">
                Launch pricing is always active, regardless of the current date.
              </p>
            </div>
            <div className={`p-4 rounded-lg border-2 transition-all ${launchPricingMode === 'FORCE_OFF' ? 'border-red-500/50 bg-red-500/10' : 'border-white/5 bg-[#1A1D25]'}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="h-3 w-3 rounded-full bg-red-500"></span>
                <p className="text-sm font-medium text-white">FORCE OFF</p>
              </div>
              <p className="text-xs text-[#8A93A6]">
                Launch pricing is always inactive. Standard pricing shown regardless of launch window.
              </p>
            </div>
          </div>

          <Alert className={launchSettings?.computed_state?.launch_pricing_active ? 'border-green-500/20 bg-green-500/10' : 'border-yellow-500/20 bg-yellow-500/10'}>
            <Info className={`h-4 w-4 ${launchSettings?.computed_state?.launch_pricing_active ? 'text-green-500' : 'text-yellow-500'}`} />
            <AlertDescription className="text-sm text-[#8A93A6]">
              <strong className="text-white">Current Status:</strong>{' '}
              Launch pricing is{' '}
              <span className={launchSettings?.computed_state?.launch_pricing_active ? 'text-green-400' : 'text-yellow-400'}>
                {launchSettings?.computed_state?.launch_pricing_active ? 'ACTIVE' : 'INACTIVE'}
              </span>
              {' '}(Mode: {launchPricingMode}, Phase: {launchSettings?.computed_state?.phase})
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="mt-6 flex gap-4">
        <Button variant="outline" onClick={() => { loadLaunchSettings(); loadStripeStatus(); }} disabled={loading}>
          Refresh Settings
        </Button>
        <a href="/pricing" target="_blank" rel="noopener noreferrer">
          <Button variant="outline">View Public Pricing Page</Button>
        </a>
      </div>
    </div>
  );
}
